/**
 * Upload-row column derivation — the SINGLE source of truth shared by the
 * UploadRowsDrawer (chart-when-chartable) and the correction value picker
 * (which column supplies a correction's before-value + period_start).
 *
 * Previously these heuristics were duplicated as private copies in
 * `correctionPick.ts` and `UploadRowsDrawer.tsx`, and the `DATE_KEY_RE`
 * regexes had diverged (the picker matched `period`, the drawer did not) — so
 * a column named `period` was treated as the date column when picking a
 * correction's gas-day but not in the drawer the operator just viewed,
 * mis-attributing the correction's `period_start`. Keeping one definition here
 * makes the two surfaces agree by construction (BIO-144 #3).
 */
import type { UploadRowEntry } from "../api/types";

/** Column names that denote a date/period column. */
export const DATE_KEY_RE = /(date|day|period|_on)$/i;
/** Anchored full `YYYY-MM-DD` match. */
export const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
/** Leading-`YYYY-MM-DD` match (value may carry a time/offset suffix). */
export const ISO_DAY_PREFIX_RE = /^\d{4}-\d{2}-\d{2}/;

/** Union of `data` keys across the rows, in first-seen order. */
export function deriveColumns(rows: UploadRowEntry[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of rows) {
    for (const k of Object.keys(r.data)) {
      if (!seen.has(k)) {
        seen.add(k);
        out.push(k);
      }
    }
  }
  return out;
}

export function looksNumeric(value: unknown): boolean {
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "string" && value.trim() !== "") return Number.isFinite(Number(value));
  return false;
}

export function looksDate(key: string, value: unknown): boolean {
  if (DATE_KEY_RE.test(key)) return true;
  return typeof value === "string" && ISO_DAY_PREFIX_RE.test(value);
}

/** First date-like column among the rows' columns, if any. */
export function detectDateColumn(rows: UploadRowEntry[], columns: string[]): string | null {
  const sample = rows[0]?.data ?? {};
  return columns.find((c) => looksDate(c, sample[c])) ?? null;
}

/** Numeric value columns (excluding the detected date column). */
export function detectNumericColumns(
  rows: UploadRowEntry[],
  columns: string[],
  dateColumn: string | null,
): string[] {
  const sample = rows[0]?.data ?? {};
  return columns.filter((c) => c !== dateColumn && looksNumeric(sample[c]));
}
